import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { useSolution } from '../../context/SolutionContext';
import { 
  FiZoomIn, FiZoomOut, FiMaximize, FiDownload, 
  FiShare2, FiLayers, FiGrid, FiGitBranch 
} from 'react-icons/fi';

const ArchitectureDiagram = () => {
  const { state, dispatch } = useSolution();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [showDataFlow, setShowDataFlow] = useState(true);
  const [showRelationships, setShowRelationships] = useState(true);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ 
          width: Math.max(rect.width, 800), 
          height: Math.max(rect.height, 600) 
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    const observer = new ResizeObserver(updateDimensions);
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      observer.disconnect();
    };
  }, []);

  const checkCompatibility = useCallback((comp1, comp2) => {
    if (!comp1?.tags || !comp2?.tags) return false;
    const tags1 = new Set(comp1.tags);
    const tags2 = new Set(comp2.tags);
    for (const tag of tags1) {
      if (tags2.has(tag)) return true;
    }
    return false;
  }, []);

  const getCompatibilityStrength = useCallback((comp1, comp2) => {
    const tags1 = new Set(comp1.tags || []);
    const tags2 = new Set(comp2.tags || []);
    let overlap = 0;
    for (const tag of tags1) {
      if (tags2.has(tag)) overlap++;
    }
    return Math.min(overlap / 3, 1);
  }, []);

  // Build architecture data using useMemo (NOT useCallback)
  const architecture = useMemo(() => {
    if (!state.selectedComponents || state.selectedComponents.length === 0) return null;

    const layerOrder = [
      { id: 'layer10', name: 'Application & Interface', y: 50, color: '#8b5cf6', icon: '🎯' },
      { id: 'layer11', name: 'Observability & Security', y: 150, color: '#ef4444', icon: '🛡️' },
      { id: 'layer12', name: 'Governance & Policy', y: 250, color: '#f59e0b', icon: '⚖️' },
      { id: 'layer6', name: 'Model & Intelligence', y: 350, color: '#3b82f6', icon: '🧠' },
      { id: 'layer5', name: 'Data Processing', y: 450, color: '#10b981', icon: '🔄' },
      { id: 'layer4', name: 'Data Storage', y: 550, color: '#06b6d4', icon: '💾' },
      { id: 'layer3', name: 'Data Acquisition', y: 650, color: '#f97316', icon: '📡' },
      { id: 'layer8', name: 'Execution Engine', y: 750, color: '#ec4899', icon: '⚡' },
      { id: 'layer1', name: 'Physical Infrastructure', y: 850, color: '#64748b', icon: '🔧' }
    ];

    const nodes = [];
    const links = [];
    const nodeMap = new Map();

    // Group components by layer
    const layerGroups = {};
    state.selectedComponents.forEach(comp => {
      if (!layerGroups[comp.layerId]) {
        layerGroups[comp.layerId] = [];
      }
      layerGroups[comp.layerId].push(comp);
    });

    // Create layer containers and component nodes
    layerOrder.forEach(layerDef => {
      const components = layerGroups[layerDef.id] || [];
      
      // Layer container node
      const layerNode = {
        id: `layer-container-${layerDef.id}`,
        name: layerDef.name,
        type: 'layer-container',
        icon: layerDef.icon,
        color: layerDef.color,
        x: dimensions.width / 2,
        y: layerDef.y,
        width: dimensions.width - 200,
        height: 80,
        layerId: layerDef.id,
        componentCount: components.length
      };
      nodes.push(layerNode);
      nodeMap.set(layerNode.id, layerNode);

      // Component nodes within layer
      components.forEach((comp, index) => {
        const totalComponents = components.length;
        const spacing = (layerNode.width - 100) / Math.max(totalComponents, 1);
        const startX = 100 + (spacing / 2);
        
        const compNode = {
          id: `comp-node-${comp.id}`,
          name: comp.name,
          type: 'component',
          icon: comp.icon || '🔧',
          color: layerDef.color,
          x: startX + (index * spacing),
          y: layerDef.y + 40,
          width: 120,
          height: 60,
          component: comp,
          layerId: layerDef.id,
          quantity: comp.quantity || 1
        };
        nodes.push(compNode);
        nodeMap.set(compNode.id, compNode);

        // Link layer container to component
        links.push({
          source: layerNode.id,
          target: compNode.id,
          type: 'contains',
          direction: 'top-down'
        });
      });
    });

    // Add data flow links between layers
    if (showDataFlow) {
      for (let i = 0; i < layerOrder.length - 1; i++) {
        const currentLayer = layerOrder[i];
        const nextLayer = layerOrder[i + 1];
        
        const currentComponents = layerGroups[currentLayer.id] || [];
        const nextComponents = layerGroups[nextLayer.id] || [];
        
        if (currentComponents.length > 0 && nextComponents.length > 0) {
          currentComponents.forEach((comp1, idx1) => {
            const targetIdx = Math.min(idx1, nextComponents.length - 1);
            const comp2 = nextComponents[targetIdx];
            
            links.push({
              source: `comp-node-${comp1.id}`,
              target: `comp-node-${comp2.id}`,
              type: 'data-flow',
              direction: 'downward',
              label: 'data flow'
            });
          });
        }
      }
    }

    // Add relationship links
    if (showRelationships) {
      Object.values(layerGroups).forEach(components => {
        for (let i = 0; i < components.length; i++) {
          for (let j = i + 1; j < components.length; j++) {
            if (checkCompatibility(components[i], components[j])) {
              links.push({
                source: `comp-node-${components[i].id}`,
                target: `comp-node-${components[j].id}`,
                type: 'relationship',
                direction: 'horizontal',
                label: 'compatible',
                strength: getCompatibilityStrength(components[i], components[j])
              });
            }
          }
        }
      });
    }

    return { nodes, links, nodeMap, layerOrder };
  }, [state.selectedComponents, dimensions, showDataFlow, showRelationships, checkCompatibility, getCompatibilityStrength]);

  // Render diagram
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !architecture) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Create defs
    const defs = svg.append('defs');

    // Arrow markers
    const markerColors = {
      'contains': '#94a3b8',
      'data-flow': '#3b82f6',
      'relationship': '#10b981'
    };

    Object.entries(markerColors).forEach(([type, color]) => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 25)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', color);
    });

    // Glow filter
    const glowFilter = defs.append('filter')
      .attr('id', 'glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    glowFilter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'blur');
    
    glowFilter.append('feComposite')
      .attr('in', 'SourceGraphic')
      .attr('in2', 'blur')
      .attr('operator', 'over');

    // Shadow filter
    const shadowFilter = defs.append('filter')
      .attr('id', 'shadow')
      .attr('x', '-20%')
      .attr('y', '-20%')
      .attr('width', '140%')
      .attr('height', '140%');
    
    shadowFilter.append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '2')
      .attr('stdDeviation', '3')
      .attr('flood-color', '#000')
      .attr('flood-opacity', '0.15');

    // Layer gradients
    architecture.layerOrder.forEach(layer => {
      const gradient = defs.append('linearGradient')
        .attr('id', `layer-gradient-${layer.id}`)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%');
      
      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', layer.color)
        .attr('stop-opacity', 0.08);
      
      gradient.append('stop')
        .attr('offset', '50%')
        .attr('stop-color', layer.color)
        .attr('stop-opacity', 0.04);
      
      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', layer.color)
        .attr('stop-opacity', 0.08);
    });

    // Main group
    const mainGroup = svg.append('g')
      .attr('transform', `translate(${transform.x},${transform.y}) scale(${transform.k})`);

    // Zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 3])
      .on('zoom', (event) => {
        setTransform(event.transform);
        mainGroup.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Tooltip
    const tooltip = d3.select(containerRef.current)
      .append('div')
      .attr('class', 'architecture-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', 'white')
      .style('border', '1px solid #e2e8f0')
      .style('border-radius', '8px')
      .style('padding', '12px')
      .style('box-shadow', '0 10px 25px rgba(0,0,0,0.15)')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('max-width', '300px');

    // Draw links
    const linkElements = mainGroup.append('g')
      .selectAll('.link')
      .data(architecture.links)
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', d => {
        const source = architecture.nodeMap.get(d.source);
        const target = architecture.nodeMap.get(d.target);
        if (!source || !target) return '';
        
        if (d.type === 'relationship') {
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const dr = Math.sqrt(dx * dx + dy * dy) * 1.5;
          return `M${source.x},${source.y + 30}A${dr},${dr} 0 0,1 ${target.x},${target.y + 30}`;
        } else {
          const sourceY = source.type === 'layer-container' ? source.y + source.height/2 : source.y + source.height/2;
          const targetY = target.type === 'layer-container' ? target.y - target.height/2 : target.y - target.height/2;
          const mx = (source.x + target.x) / 2;
          return `M${source.x},${sourceY} C${mx},${sourceY} ${mx},${targetY} ${target.x},${targetY}`;
        }
      })
      .attr('fill', 'none')
      .attr('stroke', d => markerColors[d.type] || '#94a3b8')
      .attr('stroke-width', d => {
        if (d.type === 'data-flow') return 2;
        if (d.type === 'relationship') return (d.strength || 0.5) * 3;
        return 1.5;
      })
      .attr('stroke-dasharray', d => {
        if (d.type === 'data-flow') return '6,4';
        if (d.type === 'relationship') return '4,4';
        return 'none';
      })
      .attr('opacity', d => {
        if (d.type === 'contains') return 0.4;
        if (d.type === 'data-flow') return 0.6;
        if (d.type === 'relationship') return d.strength || 0.5;
        return 0.5;
      })
      .attr('marker-end', d => `url(#arrow-${d.type})`);

    // Draw layer containers
    const layerContainers = mainGroup.selectAll('.layer-container')
      .data(architecture.nodes.filter(n => n.type === 'layer-container'))
      .enter()
      .append('g')
      .attr('class', 'layer-container')
      .attr('transform', d => `translate(${d.x - d.width/2}, ${d.y - d.height/2})`);

    layerContainers.append('rect')
      .attr('width', d => d.width)
      .attr('height', d => d.height)
      .attr('rx', 12)
      .attr('ry', 12)
      .attr('fill', d => `url(#layer-gradient-${d.layerId})`)
      .attr('stroke', d => d.color)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '8,4')
      .attr('filter', 'url(#shadow)');

    layerContainers.append('text')
      .attr('x', 20)
      .attr('y', 25)
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .attr('fill', d => d.color)
      .text(d => `${d.icon} ${d.name}`);

    layerContainers.append('text')
      .attr('x', d => d.width - 20)
      .attr('y', 25)
      .attr('text-anchor', 'end')
      .attr('font-size', '11px')
      .attr('fill', '#64748b')
      .text(d => `${d.componentCount} components`);

    // Draw component nodes
    const componentNodes = mainGroup.selectAll('.component-node')
      .data(architecture.nodes.filter(n => n.type === 'component'))
      .enter()
      .append('g')
      .attr('class', 'component-node')
      .attr('transform', d => `translate(${d.x - d.width/2}, ${d.y - d.height/2})`)
      .style('cursor', 'pointer');

    componentNodes.append('rect')
      .attr('width', d => d.width)
      .attr('height', d => d.height)
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('fill', 'white')
      .attr('stroke', d => d.color)
      .attr('stroke-width', 2)
      .attr('filter', 'url(#shadow)');

    componentNodes.append('text')
      .attr('x', d => d.width / 2)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '22px')
      .text(d => d.icon);

    componentNodes.append('text')
      .attr('x', d => d.width / 2)
      .attr('y', 45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', '600')
      .attr('fill', '#1f2937')
      .text(d => d.name.length > 18 ? d.name.substring(0, 16) + '...' : d.name);

    // Quantity badge
    componentNodes.append('circle')
      .attr('cx', d => d.width - 10)
      .attr('cy', 10)
      .attr('r', 8)
      .attr('fill', d => d.color)
      .attr('opacity', 0.9);

    componentNodes.append('text')
      .attr('x', d => d.width - 10)
      .attr('y', 13)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .text(d => d.quantity || 1);

    // Click handler
    componentNodes.on('click', (event, d) => {
      event.stopPropagation();
      setSelectedNode(d);
      if (d.component) {
        dispatch({ type: 'SHOW_COMPONENT_DETAIL', payload: d.component });
      }
    });

    // Hover effects
    componentNodes
      .on('mouseenter', function(event, d) {
        d3.select(this)
          .select('rect')
          .attr('stroke-width', 3)
          .attr('filter', 'url(#glow)');
        
        setHoveredNode(d);

        const [x, y] = d3.pointer(event, containerRef.current);
        tooltip
          .style('visibility', 'visible')
          .style('left', `${x + 15}px`)
          .style('top', `${y + 15}px`)
          .html(`
            <div style="font-weight: bold; margin-bottom: 8px;">
              ${d.icon} ${d.name}
            </div>
            <div style="font-size: 12px; color: #64748b; margin-bottom: 4px;">
              ${d.component?.purpose || ''}
            </div>
            <div style="font-size: 12px; color: #3b82f6; font-weight: 600;">
              Qty: ${d.quantity || 1} | Cost: $${((d.component?.cost?.low || 0) * (d.quantity || 1)).toFixed(0)} - $${((d.component?.cost?.high || 0) * (d.quantity || 1)).toFixed(0)}
            </div>
          `);
      })
      .on('mousemove', function(event, d) {
        const [x, y] = d3.pointer(event, containerRef.current);
        tooltip
          .style('left', `${x + 15}px`)
          .style('top', `${y + 15}px`);
      })
      .on('mouseleave', function() {
        d3.select(this)
          .select('rect')
          .attr('stroke-width', 2)
          .attr('filter', 'url(#shadow)');
        
        setHoveredNode(null);
        tooltip.style('visibility', 'hidden');
      });

    // Legend
    const legend = svg.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(20, ${dimensions.height - 80})`);

    const legendItems = [
      { color: '#94a3b8', label: 'Contains', dash: 'none' },
      { color: '#3b82f6', label: 'Data Flow', dash: '6,4' },
      { color: '#10b981', label: 'Compatible', dash: '4,4' }
    ];

    legendItems.forEach((item, i) => {
      const g = legend.append('g')
        .attr('transform', `translate(0, ${i * 22})`);
      
      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 25)
        .attr('y2', 0)
        .attr('stroke', item.color)
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', item.dash);
      
      g.append('text')
        .attr('x', 35)
        .attr('y', 4)
        .attr('font-size', '11px')
        .attr('fill', '#64748b')
        .text(item.label);
    });

    // Cleanup
    return () => {
      tooltip.remove();
    };

  }, [architecture, dimensions, transform, dispatch]);

  // Handle export functions
  const handleExportSVG = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ai-solution-architecture.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setTransform({ x: 0, y: 0, k: 1 });
    d3.select(svgRef.current)
      .transition()
      .duration(500)
      .call(d3.zoom().transform, d3.zoomIdentity);
  };

  const handleZoomIn = () => {
    const newK = Math.min(transform.k * 1.2, 3);
    setTransform({ ...transform, k: newK });
    d3.select(svgRef.current)
      .transition()
      .duration(300)
      .call(d3.zoom().transform, d3.zoomIdentity.scale(newK));
  };

  const handleZoomOut = () => {
    const newK = Math.max(transform.k / 1.2, 0.1);
    setTransform({ ...transform, k: newK });
    d3.select(svgRef.current)
      .transition()
      .duration(300)
      .call(d3.zoom().transform, d3.zoomIdentity.scale(newK));
  };

  const hasComponents = state.selectedComponents && state.selectedComponents.length > 0;

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full bg-gradient-to-br from-gray-50 via-white to-gray-100"
      style={{ minHeight: '600px', position: 'relative' }}
    >
      {/* Toolbar */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
        <div className="bg-white/90 backdrop-blur-lg rounded-xl shadow-lg border border-gray-200 p-2 flex flex-col gap-1">
          <button onClick={handleZoomIn} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" title="Zoom In">
            <FiZoomIn className="w-4 h-4" />
          </button>
          <button onClick={handleZoomOut} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" title="Zoom Out">
            <FiZoomOut className="w-4 h-4" />
          </button>
          <button onClick={handleReset} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" title="Reset View">
            <FiMaximize className="w-4 h-4" />
          </button>
          <div className="border-t border-gray-200 my-1"></div>
          <button 
            onClick={() => setShowDataFlow(!showDataFlow)} 
            className={`p-2 rounded-lg transition-colors ${showDataFlow ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
            title="Toggle Data Flow"
          >
            <FiGitBranch className="w-4 h-4" />
          </button>
          <button 
            onClick={() => setShowRelationships(!showRelationships)} 
            className={`p-2 rounded-lg transition-colors ${showRelationships ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100'}`}
            title="Toggle Relationships"
          >
            <FiLayers className="w-4 h-4" />
          </button>
          <div className="border-t border-gray-200 my-1"></div>
          <button onClick={handleExportSVG} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" title="Export SVG">
            <FiDownload className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-lg rounded-xl shadow-lg border border-gray-200 px-4 py-3 z-20">
        <div className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <span className="text-2xl">🏗️</span>
          Solution Architecture Diagram
        </div>
        <div className="text-xs text-gray-500">
          {state.selectedComponents.length} components • {new Set(state.selectedComponents.map(c => c.layerId)).size} layers
        </div>
      </div>

      {/* Empty State */}
      {!hasComponents && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center bg-white rounded-xl shadow-lg p-8 max-w-md">
            <div className="text-6xl mb-4">🏗️</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Build Your Solution Architecture
            </h3>
            <p className="text-gray-500">
              Select components from the sidebar to generate a professional architecture diagram
            </p>
          </div>
        </div>
      )}

      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        className="w-full h-full"
        style={{ cursor: hasComponents ? 'grab' : 'default' }}
      />
    </div>
  );
};

export default ArchitectureDiagram;